import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 * @property {HTMLElement} cartBubbleStatus - スクリーンリーダー向けのカート件数テキスト要素。
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount', 'cartBubbleStatus'];
  cartBubbleStatusLabel = '';

  /** @type {number} */
  get currentCartCount() {
    const count = Number.parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
    return Number.isFinite(count) ? count : 0;
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
    const statusLabel = this.getCartBubbleStatusLabel();
    this.refs.cartBubbleStatus.textContent = statusLabel ? `${statusLabel} ${value}` : String(value);
  }

  connectedCallback() {
    super.connectedCallback();

    this.getCartBubbleStatusLabel();
    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  getCartBubbleStatusLabel() {
    if (this.cartBubbleStatusLabel) return this.cartBubbleStatusLabel;

    const statusText = this.refs.cartBubbleStatus.textContent?.trim() ?? '';
    const match = statusText.match(/^([\s\S]*?[:：]).*$/);

    this.cartBubbleStatusLabel = match ? match[1] : statusText;

    return this.cartBubbleStatusLabel;
  }

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    const normalizedItemCount = Number(itemCount);
    const safeItemCount = Number.isFinite(normalizedItemCount) ? normalizedItemCount : 0;
    const nextCartCount = comingFromProductForm ? this.currentCartCount + safeItemCount : safeItemCount;

    this.refs.cartBubbleCount.classList.toggle('hidden', nextCartCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', nextCartCount === 0);
    this.refs.cartBubble.classList.toggle('cart-bubble--animating', nextCartCount > 0 && animate);

    this.currentCartCount = nextCartCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', nextCartCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate) return;
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem('cart-count');
    const visibleCount = this.refs.cartBubbleCount.textContent;

    if (sessionStorageCount === visibleCount || sessionStorageCount === null) return;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
